import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import { logEvent } from "./history";
import { Container, Button, Form, Card, Row, Col, Spinner, Accordion, Badge, Alert } from "react-bootstrap";
import MultiFileUpload from "./MultiFileUpload"; 
import { 
  Link45deg, BoxArrowInRight, ArrowsExpand, ArrowsCollapse, 
  Clock, Image, FileText, CheckCircleFill, GearFill
} from "react-bootstrap-icons";

export default function ManagerReview() {
  const { id } = useParams(); // This is the request_number (e.g., 101)
  const navigate = useNavigate();
  
  // --- UI & Submission State ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [activeKeys, setActiveKeys] = useState([]);
  const [showExtraLocales, setShowExtraLocales] = useState(false);
  const [filteredLocales, setFilteredLocales] = useState([]);

  // --- Database Identity State ---
  const [requestUuid, setRequestUuid] = useState(null); // The real PK for relations

  // --- Unified Form State ---
  const [formData, setFormData] = useState({
    title: "", description: "", projectId: "", contentTypeId: "",
    formatIds: [], sourceLanguageId: "", targetLanguageIds: [],
    deadline: "", wordCount: "", duration: "", imageCount: "",
    externalLinks: "", attachments: [], internal_notes: ""
  });

  const [snapshot, setSnapshot] = useState({
    languages: [], projects: [], contentTypes: [], formats: []
  });

  useEffect(() => {
    async function fetchData() {
      setIsLoadingData(true);
      try {
        const [langs, projs, types, forms] = await Promise.all([
          supabase.from("languages").select("*").order('region'),
          supabase.from("projects").select("*").order('name'),
          supabase.from("content_types").select("*").order('name'),
          supabase.from("formats").select("*").order('name'),
        ]);

        const lookups = { 
          languages: langs.data || [], projects: projs.data || [], 
          contentTypes: types.data || [], formats: forms.data || [] 
        };
        setSnapshot(lookups);

        // Fetch by request_number (integer from URL)
        const { data: req, error } = await supabase
          .from("requests")
          .select("*")
          .eq("request_number", id)
          .single();

        if (error) throw error;

        // CRITICAL: Save the actual UUID primary key for the jobs relation
        setRequestUuid(req.id);

        setFormData({
          title: req.title || "",
          description: req.description || "",
          projectId: req.project_id || "",
          contentTypeId: req.content_type_id || "",
          formatIds: req.format_ids || [],
          sourceLanguageId: req.source_language || "",
          targetLanguageIds: req.target_languages || [],
          deadline: req.deadline ? req.deadline.substring(0, 16) : "",
          wordCount: req.word_count || "",
          duration: req.duration_seconds || "",
          imageCount: req.image_count || "",
          externalLinks: req.external_links || "",
          attachments: req.attachments || [],
          internal_notes: req.internal_notes || ""
        });

      } catch (err) {
        console.error("Initialization error:", err);
        navigate("/manager");
      } finally {
        setIsLoadingData(false);
      }
    }
    fetchData();
  }, [id, navigate]);

  // --- Filtering Logic ---
  useEffect(() => {
    if (isLoadingData) return;
    let availableIds = snapshot.languages.map(l => l.id);

    if (formData.projectId) {
      const p = snapshot.projects.find(x => String(x.id) === String(formData.projectId));
      if (p?.allowed_languages?.length) availableIds = availableIds.filter(id => p.allowed_languages.includes(id));
    }
    if (formData.contentTypeId) {
      const t = snapshot.contentTypes.find(x => String(x.id) === String(formData.contentTypeId));
      if (t?.allowed_languages?.length) availableIds = availableIds.filter(id => t.allowed_languages.includes(id));
    }
    formData.formatIds.forEach(fId => {
      const f = snapshot.formats.find(x => String(x.id) === String(fId));
      if (f?.allowed_languages?.length) availableIds = availableIds.filter(id => f.allowed_languages.includes(id));
    });

    setFilteredLocales(snapshot.languages.filter(l => availableIds.includes(l.id)));
  }, [formData.projectId, formData.contentTypeId, formData.formatIds, snapshot, isLoadingData]);

  // --- Logic Helpers ---
  const selectedType = snapshot.contentTypes.find(t => String(t.id) === String(formData.contentTypeId))?.name?.toLowerCase() || "";
  const selectedFormats = snapshot.formats.filter(f => formData.formatIds.includes(f.id)).map(f => f.name.toLowerCase());
  
  const isAudioVideo = selectedType.includes("audio") || selectedType.includes("video") || 
                       selectedFormats.some(f => ["mp4", "wav", "mp3", "srt"].some(ext => f.includes(ext)));
  const isGraphics = selectedType.includes("graphic") || selectedType.includes("dtp") || 
                     selectedFormats.some(f => ["psd", "ai", "figma", "png"].some(ext => f.includes(ext)));

  const groupedLocales = useMemo(() => {
    const list = showExtraLocales ? snapshot.languages : filteredLocales;
    return list.reduce((acc, lang) => {
      const region = lang.region || "Other";
      if (!acc[region]) acc[region] = [];
      acc[region].push(lang);
      return acc;
    }, {});
  }, [snapshot.languages, filteredLocales, showExtraLocales]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const toggleAll = (expand) => setActiveKeys(expand ? Object.keys(groupedLocales).map((_, i) => String(i)) : []);
  const getRegionSelectionCount = (regionLocales) => regionLocales.filter(l => formData.targetLanguageIds.includes(l.id)).length;

  // --- Validation & Job Creation Logic ---
  async function handleCreateJob(e) {
    e.preventDefault();
    if (!requestUuid) {
      alert("Request identity not loaded. Please refresh.");
      return;
    }
    
    setIsSubmitting(true);
    const { data: { user } } = await supabase.auth.getUser();

    try {
      // 1. Update Master Request using UUID
      const { error: updateError } = await supabase
        .from("requests")
        .update({
          title: formData.title, 
          description: formData.description,
          project_id: formData.projectId, 
          content_type_id: formData.contentTypeId,
          format_ids: formData.formatIds, 
          target_languages: formData.targetLanguageIds,
          deadline: formData.deadline, 
          word_count: parseInt(formData.wordCount) || 0,
          duration_seconds: isAudioVideo ? parseInt(formData.duration) : null,
          image_count: isGraphics ? parseInt(formData.imageCount) : null,
          internal_notes: formData.internal_notes, 
          attachments: formData.attachments,
          status: 'validated' // Set status to validated right here
        })
        .eq("id", requestUuid);

      if (updateError) throw updateError;

      await logEvent({
        entityType: "request",
        entityId: requestUuid,
        action: "status_changed",
        summary: "Request validated by manager",
        details: {
          status: "validated",
          title: formData.title,
          deadline: formData.deadline,
          target_languages: formData.targetLanguageIds,
        },
        actor: user,
      });

      // 2. Get current job count for this request to set the next job number
      const { data: latestJob } = await supabase
        .from('jobs')
        .select('job_number')
        .eq('request_id', requestUuid)
        .order('job_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextJobNumber = latestJob ? latestJob.job_number + 1 : 1;

      // 3. Insert Job
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .insert([{ 
          request_id: requestUuid, 
          job_number: nextJobNumber,
          job_title: formData.title, 
          deadline: formData.deadline,
          content_type_id: formData.contentTypeId,
          formats_ids: formData.formatIds,
          job_manager: user.id, 
          notes: formData.internal_notes,
          status: 'in_progress',
              source_language: formData.sourceLanguageId,
    target_languages_ids: formData.targetLanguageIds
        }])
        .select().single();

      if (jobError) throw jobError;

      await logEvent({
        entityType: "job",
        entityId: job.id,
        action: "job_generated",
        summary: `Job #${job.job_number} created from request`,
        details: {
          request_id: requestUuid,
          job_number: job.job_number,
          deadline: job.deadline,
        },
        actor: user,
      });

      // 4. Navigate directly to the new Job Detail component
      navigate(`/request/${id}/job/${job.job_number}`);
      
    } catch (err) {
      console.error(err);
      alert("Error creating job: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoadingData) return <Container className="text-center mt-5"><Spinner animation="border" variant="primary" /></Container>;

  return (
    <Container fluid className="py-5 px-lg-5" style={{ maxWidth: '1600px' }}>
      <Form onSubmit={handleCreateJob}>
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div>
            <h2 className="fw-bold m-0 display-6">Review & Create Job</h2>
            <Badge bg="warning" text="dark" className="px-3 py-2 mt-2 shadow-sm">
              Request #{id} — Manager Validation
            </Badge>
          </div>
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => navigate("/manager")}>
            Back to Queue
          </Button>
        </div>

        <Row className="g-4">
          <Col lg={8}>
            <Alert variant="info" className="rounded-4 border-0 shadow-sm mb-4">
                <CheckCircleFill className="me-2"/> <strong>Manager Override:</strong> You are finalizing client data. Submitting will validate the request and generate the initial Job container.
            </Alert>

            {/* SECTION 1: SCOPE */}
            <Card className="shadow-sm border-0 rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">1</Badge> Project Scope
                </h5>
                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted">Final Job Title</Form.Label>
                  <Form.Control size="lg" className="bg-light border-0" value={formData.title} onChange={e => handleChange("title", e.target.value)} required />
                </Form.Group>
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Project Category</Form.Label>
                    <Form.Select size="lg" className="bg-light border-0 border" value={formData.projectId} onChange={e => handleChange("projectId", e.target.value)} required>
                      {snapshot.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Content Type</Form.Label>
                    <Form.Select size="lg" className="bg-light border-0 border" value={formData.contentTypeId} onChange={e => handleChange("contentTypeId", e.target.value)} required>
                      {snapshot.contentTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </Form.Select>
                  </Col>
                </Row>
                <Form.Label className="small fw-bold text-uppercase text-muted">Expected Formats</Form.Label>
                <div className="p-3 bg-light rounded-4 border border-dashed">
                  <Row className="g-2">
                    {snapshot.formats.map(f => (
                      <Col xs={6} md={3} key={f.id}>
                        <Form.Check 
                            type="checkbox" 
                            label={f.name} 
                            id={`f-${f.id}`} 
                            checked={formData.formatIds.includes(f.id)} 
                            onChange={() => {
                                const ids = formData.formatIds;
                                handleChange("formatIds", ids.includes(f.id) ? ids.filter(i => i !== f.id) : [...ids, f.id]);
                            }} 
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              </Card.Body>
            </Card>

            {/* SECTION 2: TARGET LOCALES */}
            <Card className="shadow-sm border-0 rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">2</Badge> Target Locales
                </h5>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="fw-bold small text-muted">AVAIL: {filteredLocales.length} / SEL: {formData.targetLanguageIds.length}</div>
                    <div className="d-flex gap-2 align-items-center">
                        <Button variant="outline-primary" size="sm" onClick={() => toggleAll(true)}><ArrowsExpand size={14}/> Expand All</Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => toggleAll(false)}><ArrowsCollapse size={14}/> Collapse All</Button>
                        <Form.Check type="switch" label="Bypass" checked={showExtraLocales} onChange={e => setShowExtraLocales(e.target.checked)} className="ms-2" />
                    </div>
                </div>
                <div className="custom-scroll-container border rounded-4 p-2 bg-light" style={{maxHeight: '400px', overflowY: 'auto'}}>
                  <Accordion activeKey={activeKeys} onSelect={setActiveKeys} alwaysOpen>
                    {Object.keys(groupedLocales).map((region, idx) => (
                      <Accordion.Item eventKey={String(idx)} key={region} className="border-0 border-bottom bg-transparent">
                        <Accordion.Header>
                            <div className="d-flex justify-content-between w-100 pe-3 align-items-center">
                                <span className="fw-bold small">{region}</span>
                                <Badge bg={getRegionSelectionCount(groupedLocales[region]) > 0 ? "primary" : "light"} text={getRegionSelectionCount(groupedLocales[region]) > 0 ? "white" : "dark"} pill>
                                    {getRegionSelectionCount(groupedLocales[region])} / {groupedLocales[region].length}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className="p-0 bg-white rounded-3 mt-1 mx-2 mb-2 shadow-sm">
                          {groupedLocales[region].map(lang => (
                            <div key={lang.id} className="p-2 px-3 border-bottom d-flex align-items-center hover-bg">
                              <Form.Check 
                                id={`l-${lang.id}`} 
                                label={`${lang.flag} ${lang.name}`} 
                                checked={formData.targetLanguageIds.includes(lang.id)} 
                                onChange={() => {
                                  const ids = formData.targetLanguageIds;
                                  handleChange("targetLanguageIds", ids.includes(lang.id) ? ids.filter(i => i !== lang.id) : [...ids, lang.id]);
                                }}
                                className="w-100"
                              />
                            </div>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </div>
              </Card.Body>
            </Card>

            {/* SECTION 3: SPECIFICATIONS */}
            <Card className="shadow-sm border-0 rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">3</Badge> Metrics & Specs
                </h5>
                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted">Client Instructions</Form.Label>
                  <Form.Control as="textarea" rows={3} className="bg-light border-0" value={formData.description} onChange={e => handleChange("description", e.target.value)} />
                </Form.Group>
                <Row className="g-3">
                  <Col md={4}><Form.Label className="small fw-bold text-uppercase text-muted"><FileText className="me-1"/> Word Count</Form.Label><Form.Control type="number" className="border" value={formData.wordCount} onChange={e => handleChange("wordCount", e.target.value)}/></Col>
                  {isAudioVideo && <Col md={4}><Form.Label className="small fw-bold text-uppercase text-muted"><Clock className="me-1"/> Duration (s)</Form.Label><Form.Control type="number" className="border" value={formData.duration} onChange={e => handleChange("duration", e.target.value)}/></Col>}
                  {isGraphics && <Col md={4}><Form.Label className="small fw-bold text-uppercase text-muted"><Image className="me-1"/> Image Qty</Form.Label><Form.Control type="number" className="border" value={formData.imageCount} onChange={e => handleChange("imageCount", e.target.value)}/></Col>}
                  <Col md={4}><Form.Label className="small fw-bold text-uppercase text-muted">Deadline</Form.Label><Form.Control type="datetime-local" className="border" value={formData.deadline} onChange={e => handleChange("deadline", e.target.value)}/></Col>
                </Row>
              </Card.Body>
            </Card>

            {/* SECTION 4: PM NOTES */}
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">4</Badge> Fulfillment Instructions
                </h5>
                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-uppercase text-primary"><GearFill className="me-1"/> Internal Notes (Linguist Facing)</Form.Label>
                  <Form.Control as="textarea" rows={4} value={formData.internal_notes} onChange={e => handleChange("internal_notes", e.target.value)} placeholder="Enter instructions..." className="border-primary border-opacity-25 shadow-sm" />
                </Form.Group>
                <div className="p-3 bg-light rounded-4 border mb-4">
                  <Form.Label className="small fw-bold text-muted"><Link45deg/> Asset Links</Form.Label>
                  <Form.Control type="text" value={formData.externalLinks} onChange={e => handleChange("externalLinks", e.target.value)}/>
                </div>
                <MultiFileUpload onFilesUpdated={(files) => handleChange("attachments", files)} existingFiles={formData.attachments} />
              </Card.Body>
            </Card>
          </Col>

          {/* SIDEBAR ACTION */}
          <Col lg={4}>
            <div className="sticky-top" style={{ top: '100px', zIndex: 10 }}>
              <Card className="shadow-sm border-0 rounded-4 overflow-hidden border border-success">
                <div className="bg-success p-4 text-white">
                  <h5 className="fw-bold mb-0">Validation Summary</h5>
                  <small className="opacity-75">Ready to assign tasks</small>
                </div>
                <Card.Body className="p-4">
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2"><span className="text-muted small">Target Locales</span><span className="fw-bold">{formData.targetLanguageIds.length}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span className="text-muted small">Word Count</span><span className="fw-bold">{formData.wordCount || 0}</span></div>
                    <div className="d-flex justify-content-between border-top pt-2 mt-2"><span className="text-muted small text-success">Initial Status</span><Badge bg="success" pill className="px-3">Validated</Badge></div>
                  </div>
                  <Button type="submit" variant="success" size="lg" className="w-100 py-3 fw-bold shadow-lg rounded-pill" disabled={isSubmitting || formData.targetLanguageIds.length === 0}>
                    {isSubmitting ? <Spinner size="sm"/> : <><BoxArrowInRight className="me-2"/> Validate & Create Job</>}
                  </Button>
                  <Button variant="link" className="w-100 mt-3 text-muted text-decoration-none small" onClick={() => navigate("/manager")}>
                    Exit without Saving
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Form>
      <style>{`
        .hover-bg:hover { background-color: #f8f9fa !important; cursor: pointer; }
        .accordion-button:not(.collapsed) { background-color: transparent; color: inherit; box-shadow: none; }
        .border-dashed { border: 2px dashed #dee2e6 !important; }
      `}</style>
    </Container>
  );
}